-- | GraphQL queries and types used for the select student page.
module API.SelStudent where

import Common

import API.Pagination (Page)
import API.Search (SearchQuery, queryVars)
import API.Types (StudentID)
import Data.Array (cons)
import GraphQL.Query (Query, buildQuery, nameQuery)
import Internal.Luxon (LDateTime)

-- | Student data used in search results in step 2.
type SelStudent =
    { id :: StudentID
    , name :: String
    , preferred_name :: Maybe String
    , tags :: Array { name :: String, color :: String }
    , last_imported_at :: Maybe LDateTime
    }

type AccountInfo r = { student :: Maybe SelStudent | r }

isMe :: forall r. AccountInfo r -> StudentID -> Boolean
isMe account id = maybe false (\s -> s.id == id) account.student

-- | Format a student name to be shown to the user
-- |
-- | This should be used instead of the name field since it also displays the preferred name.
-- |
-- | First argument should be an `AccountInfo` object. It's not given by name
-- | here to prevent a circular import.
studentName :: forall r. AccountInfo r -> SelStudent -> String
studentName account student = studentName_ account student
    (\name -> name)
    (\{ legal, preferred } -> legal <> " (" <> preferred <> ")")


-- | More advanced version of `studentName` which accepts two functions which
-- | determine how to format a name displayed either as a single name or a
-- | separate legal and parenthesized preferred name.
-- |
-- | Use `studentName` instead unless you need to display the preferred name
-- | with some HTML.
studentName_ :: forall a r. AccountInfo r -> SelStudent
    -> (String -> a)
    -> ({ legal :: String, preferred :: String } -> a)
    -> a
studentName_ account { id, name, preferred_name } simple paren = case preferred_name of
    Nothing -> simple name
    Just "" -> simple name
    Just pname | isMe account id -> simple pname
    Just pname -> paren { legal: name, preferred: pname }


-- | Adds a frontend-only "Imported" tag to the given student
tagImported :: SelStudent -> SelStudent
tagImported student@{ last_imported_at: Nothing } = student
tagImported student@{ tags, last_imported_at: Just _ } = student { tags = cons tag tags }
  where
    tag = { name: lookupTranslation "tag-imported", color: "rgb(241, 240, 242)" }

-- | Searches students in the backend based on the given `SearchStudentQuery`.
-- |
-- | Returns the first page in the collection.
searchStudents :: SearchQuery -> Query (Page SelStudent)
searchStudents q = buildQuery "students" $ queryVars q

fetchStudentById :: StudentID -> Query SelStudent
fetchStudentById id = nameQuery "StudentById" $ buildQuery "student" { id }
